/**
 * Public share pages for reports and dashboards.
 *
 * Renders executed report results (embedded in the share payload by the
 * backend) through the same ChartRenderer the app uses — no org context, no
 * API calls beyond the share resolution itself.
 */

import { Expand01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { IconButton } from '@/lib/base/buttons/button-utility';
import { EmptyState } from '@/lib/base/empty-state';
import { FullscreenModal } from '@/lib/base/overlays/fullscreen-modal';
import type { ChartType } from '@/types/reports';
import type { SharedDashboardData, SharedReportData } from '@/types/share';

import { ChartRenderer } from '../reports/ChartRenderer';
import { deriveDimensionNamesFromColumns } from '../reports/utils/dimensionNames';

/** Chart height for a standalone shared report. */
const SHARED_REPORT_CHART_HEIGHT = 480;

/** Chart height for a tile within a shared dashboard grid. */
const SHARED_DASHBOARD_TILE_HEIGHT = 280;

/** Chart height in a shared tile's expanded (focus-mode) view. */
const SHARED_FOCUS_CHART_HEIGHT = 560;

/**
 * Query param carrying the currently-expanded shared report's id — same
 * convention as the in-app dashboard, so a copied share URL reproduces the
 * expanded view.
 */
const EXPANDED_REPORT_PARAM = 'report';

const VALID_CHART_TYPES: ReadonlySet<string> = new Set([
  'bar',
  'line',
  'area',
  'pie',
  'funnel',
  'table',
  'metric',
]);

function toChartType(value: string): ChartType {
  return (VALID_CHART_TYPES.has(value) ? value : 'table') as ChartType;
}

/**
 * Shared page chrome: one compact branded bar (logo left, "Shared via Ahoy"
 * right) over the content. A separate near-empty header band plus a footer
 * pinned to the viewport bottom read as two strips of dead space around the
 * content — everything brand-related lives in the single top bar instead.
 */
function SharedPageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className='min-h-full bg-bg-primary flex flex-col'>
      <header className='flex items-center justify-between border-b border-border-secondary px-4 py-3 md:px-6'>
        <a href='https://ahoy.ai' className='flex items-center gap-2'>
          {/* ship-logo.svg is filled white (sidebar asset) — invisible on
              this light header. Use the dark text logo, inverted in dark
              mode. */}
          <img
            src='/ahoy-logo-with-text-black.svg'
            alt='Ahoy'
            className='h-6 dark:invert'
          />
        </a>
        <p className='text-sm text-fg-tertiary'>
          Shared via{' '}
          <a
            href='https://ahoy.ai'
            className='font-medium text-brand-primary hover:underline'
          >
            Ahoy
          </a>
        </p>
      </header>
      <main className='flex-1 max-w-6xl mx-auto w-full px-4 py-6 md:px-6'>
        {children}
      </main>
    </div>
  );
}

/** One report's chart (or its per-report failure state). */
function SharedReportChart({
  report,
  currency,
  height,
}: {
  report: SharedReportData;
  currency: string;
  height: number;
}) {
  const dimensionNames = useMemo(
    () =>
      report.results
        ? deriveDimensionNamesFromColumns(report.results.columns)
        : [],
    [report.results]
  );

  if (!report.results) {
    return (
      // Center the failure state in the tile so it doesn't strand at the
      // top of a tall card when the grid stretches rows to match siblings.
      <div className='flex h-full min-h-48 items-center justify-center'>
        <EmptyState
          variant='noData'
          size='sm'
          iconColor='warning'
          title="This chart couldn't be loaded"
          description='The report failed to run. Try the link again later.'
        />
      </div>
    );
  }
  return (
    // ChartRenderer enforces a 300px minimum chart width (pie especially);
    // on narrow phones the share card is slimmer than that, so the chart
    // scrolls inside its own container instead of overflowing the page.
    <div className='overflow-x-auto'>
      <ChartRenderer
        chartType={toChartType(report.chart_type)}
        currency={currency}
        nativeData={report.results}
        nativeChartType={toChartType(report.chart_type)}
        height={height}
        dimensionName={dimensionNames[0]}
        dimensionNames={dimensionNames}
      />
    </div>
  );
}

export function SharedReportPage({
  data,
}: {
  data: SharedReportData & { currency: string };
}) {
  return (
    <SharedPageShell>
      <div className='mb-5'>
        <h1 className='text-xl font-semibold text-fg-primary'>
          {data.name || 'Shared Report'}
        </h1>
        {data.description && (
          <p className='mt-1 text-sm text-fg-tertiary'>{data.description}</p>
        )}
      </div>
      <div className='rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary'>
        <SharedReportChart
          report={data}
          currency={data.currency}
          height={SHARED_REPORT_CHART_HEIGHT}
        />
      </div>
    </SharedPageShell>
  );
}

/**
 * One shared dashboard tile. Viewers can EXPAND the report into a focus
 * view — and nothing more: no editor link, no filters, no actions. The
 * share page stays read-only.
 */
function SharedDashboardTile({
  report,
  currency,
}: {
  report: SharedReportData;
  currency: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isExpanded = searchParams.get(EXPANDED_REPORT_PARAM) === report.id;

  const handleExpand = useCallback(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set(EXPANDED_REPORT_PARAM, report.id);
      return next;
    });
  }, [setSearchParams, report.id]);

  const handleExpandOpenChange = useCallback(
    (open: boolean) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (open) {
            next.set(EXPANDED_REPORT_PARAM, report.id);
          } else {
            next.delete(EXPANDED_REPORT_PARAM);
          }
          return next;
        },
        { replace: !open }
      );
    },
    [setSearchParams, report.id]
  );

  return (
    <div className='flex flex-col overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary'>
      <div className='flex items-start justify-between gap-2 border-b border-[var(--color-border-secondary)] px-4 py-3'>
        <div className='min-w-0'>
          <h2 className='truncate text-sm font-semibold text-fg-primary'>
            {report.name}
          </h2>
          {report.description && (
            <p className='truncate text-xs text-fg-tertiary'>
              {report.description}
            </p>
          )}
        </div>
        <IconButton
          icon={Expand01}
          tooltip='Expand'
          aria-label={`Expand ${report.name}`}
          size='sm'
          color='tertiary'
          onClick={handleExpand}
        />
      </div>
      <div className='flex-1 px-4 py-3'>
        <SharedReportChart
          report={report}
          currency={currency}
          height={SHARED_DASHBOARD_TILE_HEIGHT}
        />
      </div>

      {isExpanded && (
        <FullscreenModal
          isOpen
          onOpenChange={handleExpandOpenChange}
          isDismissable
          isKeyboardDismissDisabled={false}
          dialogAriaLabel={`${report.name} – expanded`}
          maxContentWidth='none'
          header={
            <div className='min-w-0'>
              <h2 className='truncate text-lg font-semibold text-fg-primary'>
                {report.name}
              </h2>
              {report.description && (
                <p className='truncate text-sm text-fg-tertiary'>
                  {report.description}
                </p>
              )}
            </div>
          }
        >
          <div className='mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6'>
            <SharedReportChart
              report={report}
              currency={currency}
              height={SHARED_FOCUS_CHART_HEIGHT}
            />
          </div>
        </FullscreenModal>
      )}
    </div>
  );
}

export function SharedDashboardPage({ data }: { data: SharedDashboardData }) {
  return (
    <SharedPageShell>
      <div className='mb-5'>
        <h1 className='text-xl font-semibold text-fg-primary'>
          {data.name || 'Shared Dashboard'}
        </h1>
        {data.description && (
          <p className='mt-1 text-sm text-fg-tertiary'>{data.description}</p>
        )}
      </div>
      {data.reports.length === 0 ? (
        <EmptyState
          variant='noData'
          size='md'
          title='No reports on this dashboard'
          description='The dashboard is empty.'
        />
      ) : (
        <div className='grid gap-4 md:grid-cols-2'>
          {data.reports.map(report => (
            <SharedDashboardTile
              key={report.id}
              report={report}
              currency={data.currency}
            />
          ))}
        </div>
      )}
    </SharedPageShell>
  );
}
